/**
 * Results View Component — JAD branded with pagination
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useResults, Result } from '../../hooks/useResults';
import { PortalHeader } from '../Layout/PortalHeader';
import * as XLSX from 'xlsx';

const classificationLabels: Record<string, string> = {
  good_leads: 'Good Leads',
  good_leads_later: 'Good Leads For Later',
  good_all: 'Good Leads',
  bad_leads: 'Bad Leads',
  not_interested: 'Not Interested',
  non_accredited: 'Non Accredited',
  dead_leads: 'Dead Leads',
  pending: 'Pending',
  all: 'All Calls',
};

const PAGE_SIZE = 25;

export function Results() {
  const { type } = useParams<{ type: string }>();
  // 'all' means no classification filter (fetch everything)
  const queryType = type === 'all' ? undefined : type;
  const { results, loading } = useResults(queryType, type === 'all' ? 5000 : 500);
  const [page, setPage] = useState(0);

  const title = type ? classificationLabels[type] || type : 'All Results';
  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const pagedResults = results.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const isAllView = type === 'all';

  const buildExportData = () => {
    const headers = [
      'Date', 'First Name', 'Last Name', 'Phone', 'Address', 'City',
      'State', 'Zip', 'Email', 'Accredited', 'Interested', 'New Investments',
      'Liquid To Invest', 'Quality', 'Summary', 'Call Duration',
      'Correct Name', 'DNC', 'Run', 'Recording URL',
      ...(isAllView ? ['Classification'] : []),
    ];

    const rows = results.map(r => {
      const row = [
        r.date?.toDate?.()?.toLocaleDateString() || '',
        r.firstName || '',
        r.lastName || '',
        (r.phone || '').replace(/^\+1/, ''),
        (r as unknown as Record<string, unknown>).address as string || '',
        (r as unknown as Record<string, unknown>).city as string || '',
        (r as unknown as Record<string, unknown>).stateGiven as string || (r as unknown as Record<string, unknown>).inputState as string || '',
        (r as unknown as Record<string, unknown>).zip as string || '',
        r.emailGiven || '',
        (r as unknown as Record<string, unknown>).accredited as string || '',
        r.interested || '',
        (r as unknown as Record<string, unknown>).newInvestments as string || '',
        (r as unknown as Record<string, unknown>).liquidToInvest as string || '',
        r.quality || '',
        r.summary || '',
        r.callDuration?.toString() || '',
        r.correctName || '',
        (r as unknown as Record<string, unknown>).dnc ? 'TRUE' : 'FALSE',
        r.run?.toString() || '',
        r.recordingUrl || '',
      ];
      if (isAllView) row.push(classificationLabels[r.classification] || r.classification || 'Pending');
      return row;
    });

    return { headers, rows };
  };

  const exportToCSV = () => {
    if (results.length === 0) return;
    const { headers, rows } = buildExportData();

    const csvRows = rows.map(r =>
      r.map(cell => {
        const s = String(cell);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      }).join(',')
    );

    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type || 'results'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToXLSX = () => {
    if (results.length === 0) return;
    const { headers, rows } = buildExportData();

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set Phone column as text to prevent scientific notation
    const phoneColIdx = headers.indexOf('Phone');
    if (phoneColIdx >= 0) {
      for (let r = 1; r <= rows.length; r++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c: phoneColIdx })];
        if (cell) { cell.t = 's'; cell.z = '@'; }
      }
    }

    // Auto-size columns
    ws['!cols'] = headers.map((h, i) => {
      const maxLen = Math.max(h.length, ...rows.map(r => String(r[i] || '').length));
      return { wch: Math.min(maxLen + 2, 50) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    XLSX.writeFile(wb, `${type || 'results'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/" className="back-link">Back</Link>
          <h1 style={{ margin: 0, fontSize: '1.2rem' }}>{title}</h1>
          {results.length > 0 && (
            <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>
              ({results.length} total)
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={exportToXLSX} className="btn btn-primary" disabled={results.length === 0}>
            Export Excel
          </button>
          <button onClick={exportToCSV} className="btn btn-secondary" disabled={results.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="container">
        {loading ? (
          <div className="loading">Loading results...</div>
        ) : results.length === 0 ? (
          <div className="loading">No results found</div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Phone</th>
                    {isAllView && <th>Classification</th>}
                    <th>Interested</th>
                    <th>Quality</th>
                    <th>Duration</th>
                    <th>Summary</th>
                    <th>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedResults.map((result) => (
                    <ResultRow key={result.id} result={result} showClassification={isAllView} />
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button onClick={() => setPage(0)} disabled={page === 0}>
                  First
                </button>
                <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                  Prev
                </button>
                <span>Page {page + 1} of {totalPages}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                  Next
                </button>
                <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
                  Last
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ResultRow({ result, showClassification }: { result: Result; showClassification?: boolean }) {
  const date = result.date?.toDate?.();
  const dateStr = date ? date.toLocaleDateString() : '';
  const name = `${result.firstName} ${result.lastName}`.trim() || 'Unknown';

  return (
    <tr>
      <td>{dateStr}</td>
      <td>{name}</td>
      <td>{result.phone}</td>
      {showClassification && <td>{classificationLabels[result.classification] || result.classification || 'Pending'}</td>}
      <td>{result.interested}</td>
      <td>{result.quality}</td>
      <td>{result.callDuration}s</td>
      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {result.summary}
      </td>
      <td>
        {result.recordingUrl && (
          <a href={result.recordingUrl} target="_blank" rel="noopener noreferrer">
            Listen
          </a>
        )}
      </td>
    </tr>
  );
}
